import { useMemo } from "react";
import { useMarketingSettings } from "@/hooks/sms/useMarketingSettings";
import { useInviteRotation } from "@/hooks/sms/useInviteRotation";
import { useInviteBatches } from "@/hooks/sms/useInviteBatches";
import { useInviteMonthStats } from "@/hooks/sms/useInviteMonthStats";
import { inviteBatchesInMonth, inviteSegments } from "@/utils/inviteForecast";
import { DEFAULT_INVITE_MESSAGE_BODY, localDate } from "@/utils/smsShared";

export interface InviteMonthSummary {
  invited: number;
  joined: number;
  optedOut: number;
  /** Whole percent, 0 when nobody was invited (never NaN). */
  joinRate: number;
  /** invited × segments per invite × sms_cost_per_segment. */
  spent: number;
  isLoading: boolean;
  isError: boolean;
}

/**
 * The Order Invite "This month" roll-up (moved from the Invitation tab to
 * Contacts): invited = Σ sent_count of this store-local month's invite batches,
 * joined / opted out = their campaign_recipients outcomes, spent = invited ×
 * the real template's segments × the per-segment cost. Shares the
 * useInviteBatches(31) cache with the Invitation tab.
 */
export const useInviteMonthSummary = (): InviteMonthSummary => {
  const settingsQuery = useMarketingSettings();
  const rotationQuery = useInviteRotation();
  const batchesQuery = useInviteBatches(31);
  const settings = settingsQuery.data;
  const rotation = rotationQuery.data;

  const monthBatches = useMemo(() => {
    if (!settings || !batchesQuery.data) return [];
    const yyyyMm = localDate(new Date(), settings.timezone).slice(0, 7);
    return inviteBatchesInMonth(batchesQuery.data, yyyyMm, settings.timezone);
  }, [settings, batchesQuery.data]);
  const monthIds = useMemo(() => monthBatches.map((b) => b.id), [monthBatches]);
  const statsQuery = useInviteMonthStats(monthIds);

  const invited = monthBatches.reduce((sum, b) => sum + (b.sent_count ?? 0), 0);
  const joined = monthIds.length > 0 ? (statsQuery.data?.joined ?? 0) : 0;
  const optedOut = monthIds.length > 0 ? (statsQuery.data?.optedOut ?? 0) : 0;
  const segments =
    settings && rotation
      ? inviteSegments(rotation.invite_message_body ?? DEFAULT_INVITE_MESSAGE_BODY, {
          storeName: settings.store_name,
          storePhone: settings.store_phone,
          storeAddress: settings.store_address,
        })
      : 0;

  return {
    invited,
    joined,
    optedOut,
    joinRate: invited > 0 ? Math.round((joined / invited) * 100) : 0,
    spent: invited * segments * (settings?.sms_cost_per_segment ?? 0),
    isLoading:
      settingsQuery.isLoading ||
      rotationQuery.isLoading ||
      batchesQuery.isLoading ||
      (monthIds.length > 0 && statsQuery.isLoading),
    isError: settingsQuery.isError || rotationQuery.isError || batchesQuery.isError || statsQuery.isError,
  };
};
